import { Tooltip, type TooltipPositions, type Variant } from "@/components/atoms/Tooltip/Tooltip";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./TooltipWrapper.scss";

export interface TooltipWrapperProps {
  children: React.ReactNode;
  tooltipText: string;
  tail?: boolean;
  variant?: Variant;
  shadow?: boolean;
  position?: TooltipPositions;
  disabled?: boolean;
  width?: number;
  className?: string;
  delay?: number;
  offset?: number;
  forceHide?: boolean;
}

export const TooltipWrapper: React.FC<TooltipWrapperProps> = ({
  children,
  tooltipText,
  tail = false,
  variant = "primary",
  shadow = false,
  position = "top",
  disabled = false,
  width,
  className = "",
  delay = 300,
  offset = 8,
  forceHide = false,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearShowTimeout = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  };

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const hideImmediately = () => {
    clearShowTimeout();
    clearHideTimeout();
    setIsVisible(false);
    setIsMounted(false);
  };

  const calculatePosition = () => {
    if (!triggerRef.current) {
      return { x: 0, y: 0 };
    }

    const rect = triggerRef.current.getBoundingClientRect();
    let x = 0;
    let y = 0;

    switch (position) {
      case "top":
        x = rect.left + rect.width / 2;
        y = rect.top;
        break;
      case "bottom":
        x = rect.left + rect.width / 2;
        y = rect.bottom;
        break;
      case "left":
        x = rect.left;
        y = rect.top + rect.height / 2;
        break;
      case "right":
        x = rect.right;
        y = rect.top + rect.height / 2;
        break;
      case "top-left":
        x = rect.left;
        y = rect.top;
        break;
      case "top-right":
        x = rect.right;
        y = rect.top;
        break;
      case "bottom-left":
        x = rect.left;
        y = rect.bottom;
        break;
      case "bottom-right":
        x = rect.right;
        y = rect.bottom;
        break;
      default:
        x = rect.left + rect.width / 2;
        y = rect.top;
    }

    return { x, y };
  };

  const handleMouseEnter = () => {
    if (disabled || forceHide || !tooltipText) {
      return;
    }

    clearHideTimeout();
    clearShowTimeout();

    showTimeoutRef.current = setTimeout(() => {
      const pos = calculatePosition();
      setTooltipPosition(pos);
      setIsMounted(true);

      // Small delay to ensure DOM is rendered before making visible
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }, delay);
  };

  const handleMouseLeave = () => {
    clearShowTimeout();
    clearHideTimeout();

    // Immediately hide, but delay unmounting for transition
    setIsVisible(false);

    hideTimeoutRef.current = setTimeout(() => {
      setIsMounted(false);
      hideTimeoutRef.current = null;
    }, 200);
  };

  useEffect(() => {
    if (disabled || forceHide || !tooltipText) {
      hideImmediately();
    }
  }, [disabled, forceHide, tooltipText]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const updatePosition = () => {
      const pos = calculatePosition();
      setTooltipPosition(pos);
    };

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMounted, position, tooltipText]);

  // Defensive global closing to avoid stuck tooltips when pointer events are interrupted (e.g. drag).
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const handleBlur = () => hideImmediately();
    const handleDragStart = () => hideImmediately();
    const handlePointerDown = () => hideImmediately();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        hideImmediately();
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hideImmediately();
      }
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("dragstart", handleDragStart, true);
    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("dragstart", handleDragStart, true);
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isMounted]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearShowTimeout();
      clearHideTimeout();
    };
  }, []);

  if (disabled) {
    return <>{children}</>;
  }

  const tooltipClasses = ["tooltip-wrapper2", className].filter(Boolean).join(" ");

  return (
    <>
      <div ref={triggerRef} className={tooltipClasses} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      {isMounted &&
        tooltipText &&
        createPortal(
          <Tooltip
            tail={tail}
            variant={variant}
            shadow={shadow}
            position={position}
            visible={isVisible}
            width={width}
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
            }}
          >
            {tooltipText}
          </Tooltip>,
          document.body,
        )}
    </>
  );
};
